import * as Sentry from '@sentry/browser';
import { useCallback } from 'react';
import {
    EVENT_INITIATORS,
    INTERACTION_TYPES,
} from '@zocdoc-frontend-common/utils-frontend-metrics';
import { resendVerificationCode } from '../api/resendVerificationCode';
import { Metrics, MFAVerificationTypes } from '../constants';
import { cannotAttemptAuthenticateChallenge } from '../utils/cannotAttemptAuthenticateChallenge';

/**
 * @param {string} email
 * @param {Functions_Shared_Fem_SendMetric} sendMetric
 * @param {function} setErrorMessage
 * @param {function} setIsCodeResent
 * @param {function} setIsRequestingCode
 * @param {function(shouldClearErrorMessageOnUpdate: boolean): void} setShouldClearErrorMessageOnUpdate - removes a blocker from clearing the error message on update
 * @param {function(shouldDisableForm: boolean): void} setShouldDisableForm - disables the verification form if the session expires or the user makes too many attempts
 * @param {string} verificationId
 * @param {string} verificationToken
 * @param {function} setVerificationCodeDestination
 * @param {function} setVerificationId
 * @param {function} setVerificationToken
 *
 * @returns {function(verificationMethod: MFA_VERIFICATION_TYPE): void}
 */
export function useResendVerificationCode({
    email,
    sendMetric,
    setErrorMessage,
    setIsCodeResent,
    setIsRequestingCode,
    setShouldClearErrorMessageOnUpdate,
    setShouldDisableForm,
    verificationId,
    verificationToken,
    setVerificationCodeDestination,
    setVerificationId,
    setVerificationToken,
}) {
    return useCallback(
        (verificationMethod = MFAVerificationTypes.sms) => {
            async function sendVerificationCode() {
                setIsRequestingCode(true);

                sendMetric({
                    action: Metrics.ClickResend,
                    element: 'MFAVerificationFooter',
                    initiator: EVENT_INITIATORS.USER,
                    interactionType: INTERACTION_TYPES.CLICK,
                    eventDetails: {
                        verificationMethod,
                    },
                });

                try {
                    const response = await resendVerificationCode({
                        email,
                        verificationId,
                        verificationToken,
                        verificationChannel: verificationMethod,
                    });

                    if (!response) {
                        sendMetric({
                            action: Metrics.ViewErrorMessage,
                            element: 'ErrorMessage',
                            initiator: EVENT_INITIATORS.USER,
                            interactionType: INTERACTION_TYPES.VIEW,
                            eventDetails: {
                                error: 'Unhandled error message.',
                                hook: 'useResendVerificationCode',
                            },
                        });

                        setIsRequestingCode(false);
                        setErrorMessage('There was a problem logging you in.');
                        setShouldClearErrorMessageOnUpdate(true);

                        return;
                    }

                    if (response.success) {
                        setVerificationCodeDestination(
                            response.verificationCodeDestination
                        );
                        setVerificationToken(response.verificationToken);
                        setVerificationId(response.verificationId);

                        setIsCodeResent(true);

                        setErrorMessage(response.bannerErrorMessage ?? '');
                        setShouldClearErrorMessageOnUpdate(
                            !response.bannerErrorMessage
                        );
                    } else {
                        sendMetric({
                            action: Metrics.ViewErrorMessage,
                            element: 'ErrorMessage',
                            initiator: EVENT_INITIATORS.USER,
                            interactionType: INTERACTION_TYPES.VIEW,
                            eventDetails: {
                                error:
                                    response?.errorMessage ||
                                    'There was a problem logging you in.',
                                hook: 'useResendVerificationCode',
                            },
                        });

                        setErrorMessage(
                            response?.errorMessage ||
                                'There was a problem logging you in.'
                        );
                        setShouldClearErrorMessageOnUpdate(true);

                        setShouldDisableForm(
                            cannotAttemptAuthenticateChallenge(
                                response?.errorCode
                            )
                        );
                    }
                } catch (err) {
                    sendMetric({
                        action: Metrics.ViewErrorMessage,
                        element: 'ErrorMessage',
                        initiator: EVENT_INITIATORS.USER,
                        interactionType: INTERACTION_TYPES.VIEW,
                        eventDetails: {
                            error: err,
                            hook: 'useResendVerificationCode',
                        },
                    });

                    Sentry.captureException(err);
                    setErrorMessage('There was a problem logging you in.');
                    setShouldClearErrorMessageOnUpdate(true);
                }

                setIsRequestingCode(false);
            }

            sendVerificationCode();
        },
        [
            setIsRequestingCode,
            sendMetric,
            email,
            verificationId,
            verificationToken,
            setErrorMessage,
            setShouldClearErrorMessageOnUpdate,
            setVerificationCodeDestination,
            setVerificationToken,
            setVerificationId,
            setIsCodeResent,
            setShouldDisableForm,
        ]
    );
}
